import React, { useState, useEffect } from 'react';
import * as SimpleIcons from 'react-icons/si';
import { IconType } from 'react-icons';

interface IntegrationLogoProps {
  domain?: string;
  name: string;
  fallbackImage?: string;
  className?: string;
  alt?: string;
}

interface LogoSource {
  type: 'clearbit' | 'simple-icon' | 'fallback';
  url?: string;
  component?: IconType;
}

const IntegrationLogo: React.FC<IntegrationLogoProps> = ({ 
  domain, 
  name, 
  fallbackImage, 
  className = "w-8 h-8",
  alt 
}) => {
  const [logoSource, setLogoSource] = useState<LogoSource | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const tryLogos = async () => {
      // 1) Try Clearbit if domain is provided
      if (domain) {
        const clearbitUrl = `https://logo.clearbit.com/${domain}`;
        const clearbitWorks = await checkImageExists(clearbitUrl);
        
        if (!isMounted) return;
        
        if (clearbitWorks) {
          setLogoSource({ type: 'clearbit', url: clearbitUrl });
          setLoading(false);
          return;
        }
      }

      // 2) Try Simple Icons (react-icons/si)
      const iconComponent = getSimpleIcon(name);
      
      if (iconComponent) {
        setLogoSource({ type: 'simple-icon', component: iconComponent });
        setLoading(false);
        return;
      }

      // 3) Fallback to our own image if provided
      if (fallbackImage) {
        setLogoSource({ type: 'fallback', url: fallbackImage });
      } else {
        // Ultimate fallback if nothing else works
        setLogoSource(null);
      }
      setLoading(false);
    };

    tryLogos();

    return () => {
      isMounted = false;
    };
  }, [domain, name, fallbackImage]);

  if (loading) {
    return <div className={`${className} bg-gray-100 animate-pulse rounded`} />;
  }

  if (logoSource?.type === 'clearbit' || logoSource?.type === 'fallback') {
    return (
      <img 
        src={logoSource.url} 
        alt={alt || name}
        className={className}
        style={{ objectFit: 'contain' }}
        onError={(e) => {
          // Extra safety: if image fails to load, try next fallback
          if (logoSource.type === 'clearbit' && fallbackImage) {
            (e.target as HTMLImageElement).src = fallbackImage;
          }
        }}
      />
    );
  }

  if (logoSource?.type === 'simple-icon' && logoSource.component) {
    const IconComponent = logoSource.component as any;
    return <IconComponent className={className} aria-label={alt || name} />;
  }

  // Final fallback: just the first letter if everything fails
  return (
    <div className={`${className} flex items-center justify-center bg-indigo-100 text-indigo-700 font-bold rounded`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// Helper: Check if image URL exists and loads
const checkImageExists = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    
    // Timeout after 2 seconds
    setTimeout(() => resolve(false), 2000);
  });
};

// Helper: Get Simple Icon component by name
const getSimpleIcon = (name: string): IconType | null => {
  // Normalize name to Simple Icons format
  // Simple Icons uses format like: SiSlack, SiGithub, SiNotion
  const normalized = normalizeIconName(name);
  
  // Check if icon exists in Simple Icons
  const icons = SimpleIcons as Record<string, IconType>;
  return icons[normalized] || null;
};

// Helper: Convert company name to Simple Icons component name
const normalizeIconName = (name: string): string => {
  // Handle some manual mappings if needed
  const manualMappings: Record<string, string> = {
    'google-analytics': 'SiGoogleanalytics',
    'google-sheets': 'SiGooglesheets',
    'quickbooks': 'SiQuickbooks',
    'salesforce': 'SiSalesforce',
    'posthog': 'SiPosthog',
  };

  if (manualMappings[name.toLowerCase()]) {
    return manualMappings[name.toLowerCase()];
  }

  // Remove spaces, hyphens, special chars and capitalize
  const cleaned = name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  
  // Simple Icons format: Si + PascalCase
  return 'Si' + cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export default IntegrationLogo;

